import clsx from 'clsx';
import { useAuth } from 'contexts/auth/auth';
import React, { useCallback, useState } from 'react';
import { Typography } from 'ui-kit';
import { getVisitedStatus, saveAction } from '../utils';
import './OnboardingItem.scss';

export enum OnboardingItemId {
  exampleStore,
  sellingFaq,
  localVendorItems,
}

interface BaseProps {
  id: string | OnboardingItemId;
  icon?: React.ReactNode;
  title: string;
  description: string | React.ReactNode;
  onClick?: (id: string | OnboardingItemId) => void;
  as?: 'button' | 'a' | 'div';
}

interface LinkProps extends BaseProps {
  as: 'a';
  href: string;
}

interface ButtonProps extends BaseProps {
  as: 'button';
}

interface DivProps extends BaseProps {
  as: 'div';
}

export type OnboardingItemProps = LinkProps | ButtonProps | DivProps;

export default function OnboardingItem({
  id,
  icon = null,
  title,
  description,
  as = 'button',
  onClick = () => null,
  ...rest
}: OnboardingItemProps) {
  const { id: ownerId } = useAuth();
  const [isVisited, setVisited] = useState(() => getVisitedStatus(id, ownerId));
  const Button = useCallback((props) => {
    return React.createElement(as, as === 'a' ? { ...props, target: '_blank' } : props);
  }, []);

  const handleClick = () => {
    saveAction(id, ownerId, (id) => {
      const newVisitedStatus = getVisitedStatus(id, ownerId);
      if (newVisitedStatus !== isVisited) {
        setVisited(newVisitedStatus);
      }
      onClick(id);
    });
  };

  return (
    <Button
      className={clsx('OnboardingItem-container', {
        [`OnboardingItem-container-visited`]: isVisited,
        [`OnboardingItem-container-clickable`]: as !== 'div',
      })}
      onClick={handleClick}
      {...rest}
    >
      <div className="OnboardingItem-container-icon">{icon}</div>
      <div className="OnboardingItem-container-details">
        <Typography variant="body1" className="OnboardingItem-container-details__title">
          {title}
        </Typography>
        <Typography variant="body2" className="OnboardingItem-container-details__description">
          {description}
        </Typography>
      </div>
    </Button>
  );
}
